import { queryClient } from '../queryClient';
import { ACADEMY_SCOPED_QUERY_ROOTS } from './keys';

/**
 * Recursively check whether a query key contains `value` anywhere, including
 * inside nested objects/arrays (e.g. `bookings.list` embeds academyId in a
 * filters object, so a shallow `key.includes(value)` would miss it).
 */
function queryKeyContainsValue(node: unknown, value: string): boolean {
  if (node === value) {
    return true;
  }
  if (Array.isArray(node)) {
    return node.some((child) => queryKeyContainsValue(child, value));
  }
  if (node && typeof node === 'object') {
    return Object.values(node).some((child) => queryKeyContainsValue(child, value));
  }
  return false;
}

/**
 * Drop cached server data for a previous academy when the user switches context.
 * Active queries for the new academy refetch via their updated query keys.
 */
export function invalidateAcademyScopedQueries(previousAcademyId?: string | null): void {
  if (!previousAcademyId) {
    return;
  }

  for (const root of ACADEMY_SCOPED_QUERY_ROOTS) {
    queryClient.removeQueries({
      queryKey: root,
      predicate: (query) => queryKeyContainsValue(query.queryKey, previousAcademyId),
    });
  }
}

/** Clear all academy-scoped cached queries (e.g. on logout — also clears via queryClient.clear()). */
export function removeAllAcademyScopedQueries(): void {
  for (const root of ACADEMY_SCOPED_QUERY_ROOTS) {
    queryClient.removeQueries({ queryKey: root });
  }
}
